'use client';

/**
 * GlassAlert Component
 *
 * An alert with glassmorphism styling and subtle animations.
 * Automatically adapts to light/dark theme.
 */

import Alert, { type AlertProps } from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import { styled, useTheme } from '@mui/material/styles';
import { AnimatePresence, motion } from 'framer-motion';
import { forwardRef } from 'react';
import { glassEffects } from '../../theme/shadows';
import { motionPresets, transitionPresets } from '../../theme/transitions';

// ============================================================================
// Types
// ============================================================================

export interface GlassAlertProps extends Omit<AlertProps, 'variant'> {
  /**
   * Alert title
   */
  title?: string;

  /**
   * Enable entrance animation
   * @default true
   */
  animate?: boolean;

  /**
   * Enable glassmorphism blur effect
   * @default true
   */
  blur?: boolean;

  /**
   * Dismiss handler - when set, shows a close button
   */
  onDismiss?: () => void;

  /**
   * Whether the alert is visible (for AnimatePresence)
   * @default true
   */
  visible?: boolean;
}

// ============================================================================
// Styled Components
// ============================================================================

interface StyledAlertProps {
  $isDark: boolean;
  $blur: boolean;
  $severity: AlertProps['severity'];
}

type SeverityKey = 'success' | 'info' | 'warning' | 'error';

const SEVERITY_COLOR_MAP: Record<SeverityKey, { dark: string; light: string }> = {
  success: { dark: 'rgba(46, 125, 50, 0.15)', light: 'rgba(46, 125, 50, 0.06)' },
  info: { dark: 'rgba(2, 136, 209, 0.15)', light: 'rgba(2, 136, 209, 0.06)' },
  warning: { dark: 'rgba(237, 108, 2, 0.15)', light: 'rgba(237, 108, 2, 0.06)' },
  error: { dark: 'rgba(211, 47, 47, 0.15)', light: 'rgba(211, 47, 47, 0.06)' },
};

const SEVERITY_BORDER_MAP: Record<SeverityKey, { dark: string; light: string }> = {
  success: { dark: 'rgba(46, 125, 50, 0.25)', light: 'rgba(46, 125, 50, 0.18)' },
  info: { dark: 'rgba(2, 136, 209, 0.25)', light: 'rgba(2, 136, 209, 0.18)' },
  warning: { dark: 'rgba(237, 108, 2, 0.25)', light: 'rgba(237, 108, 2, 0.18)' },
  error: { dark: 'rgba(211, 47, 47, 0.25)', light: 'rgba(211, 47, 47, 0.18)' },
};

const getSeverityColor = (severity: AlertProps['severity'], isDark: boolean): string => {
  const color = SEVERITY_COLOR_MAP[severity ?? 'info'];
  return isDark ? color.dark : color.light;
};

const getSeverityBorder = (severity: AlertProps['severity'], isDark: boolean): string => {
  const color = SEVERITY_BORDER_MAP[severity ?? 'info'];
  return isDark ? color.dark : color.light;
};

const StyledAlert = styled(Alert, {
  shouldForwardProp: (prop) => !prop.toString().startsWith('$'),
})<StyledAlertProps>(({ theme, $isDark, $blur, $severity }) => {
  const glass = $isDark ? glassEffects.dark : glassEffects.light;
  const isMaterial = theme.designGuideline === 'material';

  return {
    borderRadius: 12, // 12px works for both Apple and MD3
    padding: theme.spacing(1.5, 2),
    transition: transitionPresets.card,
    background: getSeverityColor($severity, $isDark),
    // MD3: no blur effect, solid surface
    backdropFilter: isMaterial ? 'none' : $blur ? glass.backdropBlur : 'none',
    WebkitBackdropFilter: isMaterial ? 'none' : $blur ? glass.backdropBlur : 'none',
    border: isMaterial
      ? `1px solid ${getSeverityBorder($severity, $isDark)}`
      : `0.5px solid ${getSeverityBorder($severity, $isDark)}`,
    boxShadow: isMaterial
      ? 'none'
      : `${glass.shadow}, inset 0 0.5px 0 ${$isDark ? 'rgba(255, 255, 255, 0.08)' : 'rgba(255, 255, 255, 0.5)'}`,

    '& .MuiAlert-icon': {
      opacity: 0.9,
    },

    '& .MuiAlert-message': {
      color: theme.palette.text.primary,
    },

    '& .MuiAlertTitle-root': {
      fontWeight: isMaterial ? 500 : 600,
      marginBottom: theme.spacing(0.5),
    },

    '& .MuiAlert-action': {
      paddingTop: 0,
    },
  };
});

// Motion wrapper
const MotionWrapper = motion.div;

// ============================================================================
// Component
// ============================================================================

export const GlassAlert = forwardRef<HTMLDivElement, GlassAlertProps>(
  (
    {
      children,
      title,
      severity = 'info',
      animate = true,
      blur = true,
      visible = true,
      onDismiss,
      onClose,
      sx,
      ...props
    },
    ref,
  ) => {
    const theme = useTheme();
    const isDark = theme.palette.mode === 'dark';

    const alertContent = (
      <StyledAlert
        ref={ref}
        severity={severity}
        $isDark={isDark}
        $blur={blur}
        $severity={severity}
        onClose={onDismiss ?? onClose}
        sx={sx}
        {...props}
      >
        {title && <AlertTitle>{title}</AlertTitle>}
        {children}
      </StyledAlert>
    );

    if (!animate) {
      return visible ? alertContent : null;
    }

    return (
      <AnimatePresence mode="wait">
        {visible && (
          <MotionWrapper
            initial={motionPresets.fadeInUp.initial}
            animate={motionPresets.fadeInUp.animate}
            exit={{ opacity: 0, y: -10 }}
            transition={motionPresets.fadeInUp.transition}
          >
            {alertContent}
          </MotionWrapper>
        )}
      </AnimatePresence>
    );
  },
);

GlassAlert.displayName = 'GlassAlert';

export default GlassAlert;
